import React, { useState } from "react";
import axios from "axios";
import Card from "components/Card/Card";
import CardFb from "components/Card/CardFb";
import CardVideo from "app/(archives)/videos/CardVideo";
import { useSelector } from "react-redux";
import Page404 from "app/not-found";
import "../../styles/styles.css";
import ChangeFbTokenButton from "./ChangeFbTokenButton";

import {
  Document,
  Packer,
  Paragraph,
  TextRun,
  ImageRun,
  Table,
  TableRow,
  TableCell,
  WidthType,
  AlignmentType,
  ExternalHyperlink,
} from "docx";


const API_URL = process.env.REACT_APP_API_URL;

const axiosInstance = axios.create({
  baseURL: API_URL,
  headers: { "Content-Type": "application/json" },
});

const PageCreerInfolettre = () => {
  const { user } = useSelector((state) => state.user);
  const today = new Date().toISOString().split("T")[0];

  const [articles, setArticles] = useState([]);
  const [videos, setVideos] = useState([]);
  const [fbPosts, setFbPosts] = useState([]);

  // Dates appliquées
  const [dateDebut, setDateDebut] = useState("");
  const [dateFin, setDateFin] = useState(today);

  // Dates saisies
  const [dateDebutInput, setDateDebutInput] = useState("");
  const [dateFinInput, setDateFinInput] = useState(today);

  // États séparés (pour éviter "tout vide" si une seule ressource plante)
  const [loadingArticles, setLoadingArticles] = useState(false);
  const [loadingVideos, setLoadingVideos] = useState(false);
  const [loadingFb, setLoadingFb] = useState(false);

  const [errorArticles, setErrorArticles] = useState("");
  const [errorVideos, setErrorVideos] = useState("");
  const [errorFb, setErrorFb] = useState("");

  const isLoading = loadingArticles || loadingVideos || loadingFb;

  /* ===================== FETCH ARTICLES ===================== */
  const fetchArticles = async (debut, fin) => {
    try {
      setLoadingArticles(true);
      setErrorArticles("");

      const res = await axiosInstance.get("api/articles/Articles");
      const data = Array.isArray(res.data) ? res.data : [];

      const filtered = data
        .filter((article) => {
          const created = new Date(article.createdAt);
          if (debut && created < new Date(debut)) return false;
          if (fin && created > new Date(fin)) return false;
          return true;
        })
        .sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt));

      setArticles(filtered);
    } catch (err) {
      console.error("Erreur chargement articles :", err);
      setErrorArticles("Impossible de charger les articles.");
      // Important: on ne vide pas la liste si ça plante (on garde l'ancien affichage)
    } finally {
      setLoadingArticles(false);
    }
  };

  /* ===================== FETCH VIDEOS ===================== */
  const fetchVideos = async (debut, fin) => {
    try {
      setLoadingVideos(true);
      setErrorVideos("");

      const res = await axiosInstance.get("api/videos");
      const data = Array.isArray(res.data) ? res.data : [];

      const filtered = data
        .filter((video) => {
          const created = new Date(video.createdAt);
          if (debut && created < new Date(debut)) return false;
          if (fin && created > new Date(fin)) return false;
          return true;
        })
        .sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt));

      setVideos(filtered);
    } catch (err) {
      console.error("Erreur chargement vidéos :", err);
      setErrorVideos("Impossible de charger les vidéos.");
    } finally {
      setLoadingVideos(false);
    }
  };

  /* ===================== FETCH FACEBOOK POSTS ===================== */
  const fetchFbPosts = async (debut, fin) => {
    try {
      setLoadingFb(true);
      setErrorFb("");

      const res = await axiosInstance.get("api/fbposts", {
        params: { dateDebut: debut || "", dateFin: fin || "" },
      });

      const posts = Array.isArray(res.data?.posts) ? res.data.posts : [];

      // Déduplication par href
      const map = new Map();
      for (const p of posts) {
        if (p?.href && !map.has(p.href)) map.set(p.href, p);
      }

      const uniquePosts = Array.from(map.values()).sort(
        (a, b) => new Date(b.createdAt) - new Date(a.createdAt)
      );

      setFbPosts(uniquePosts);
    } catch (err) {
      console.error("Erreur chargement posts Facebook :", err);
      setErrorFb("Impossible de charger les posts Facebook.");
    } finally {
      setLoadingFb(false);
    }
  };

  /* ===================== SUBMIT (déclenche uniquement au clic) ===================== */
  const handleSubmitDates = () => {
    const debut = dateDebutInput;
    const fin = dateFinInput;

    // On applique la période tout de suite (UI + export Word cohérents)
    setDateDebut(debut);
    setDateFin(fin);

    // On lance les 3 fetchs indépendamment
    fetchArticles(debut, fin);
    fetchVideos(debut, fin);
    fetchFbPosts(debut, fin);
  };

  /* ===================== WORD EXPORT ===================== */
  const generateWord = async () => {
    const rowsArticles = [];

    for (const article of articles) {
      const imageUrl = article.image?.startsWith("http")
        ? article.image
        : `https://ahuncycle.com${article.image}`;

      const link = article.href?.startsWith("http")
        ? article.href
        : `https://ahuncycle.com${article.href}`;

      let imageBuffer = null;
      try {
        const imgRes = await fetch(imageUrl);
        imageBuffer = await imgRes.arrayBuffer();
      } catch (e) {
        console.warn("Image article non chargée :", imageUrl);
      }

      rowsArticles.push(
        new TableRow({
          children: [
            new TableCell({
              width: { size: 35, type: WidthType.PERCENTAGE },
              children: imageBuffer
                ? [
                    new Paragraph({
                      children: [
                        new ExternalHyperlink({
                          link,
                          children: [
                            new ImageRun({
                              data: imageBuffer,
                              transformation: { width: 180, height: 120 },
                            }),
                          ],
                        }),
                      ],
                    }),
                  ]
                : [],
            }),
            new TableCell({
              width: { size: 65, type: WidthType.PERCENTAGE },
              children: [
                new Paragraph({
                  children: [
                    new ExternalHyperlink({
                      link,
                      children: [
                        new TextRun({
                          text: article.titre,
                          bold: true,
                          underline: {},
                          font: "Calibri",
                          size: 24,
                        }),
                      ],
                    }),
                  ],
                }),
                new Paragraph({
                  children: [
                    new TextRun({
                      text: article.description || "",
                      font: "Calibri",
                      size: 22,
                    }),
                  ],
                }),
              ],
            }),
          ],
        })
      );
    }

    const rowsFbPosts = [];
    for (const post of fbPosts) {
      const imageUrl = post.image?.startsWith("http")
        ? post.image
        : `https://ahuncycle.com${post.image}`;

      const link = post.href?.startsWith("http")
        ? post.href
        : `https://facebook.com${post.href}`;

      let imageBuffer = null;
      try {
        if (imageUrl) {
          const imgRes = await fetch(imageUrl);
          imageBuffer = await imgRes.arrayBuffer();
        }
      } catch (e) {
        console.warn("Image post FB non chargée :", imageUrl);
      }

      rowsFbPosts.push(
        new TableRow({
          children: [
            new TableCell({
              width: { size: 35, type: WidthType.PERCENTAGE },
              children: imageBuffer
                ? [
                    new Paragraph({
                      children: [
                        new ExternalHyperlink({
                          link,
                          children: [
                            new ImageRun({
                              data: imageBuffer,
                              transformation: { width: 180, height: 120 },
                            }),
                          ],
                        }),
                      ],
                    }),
                  ]
                : [],
            }),
            new TableCell({
              width: { size: 65, type: WidthType.PERCENTAGE },
              children: [
                new Paragraph({
                  children: [
                    new ExternalHyperlink({
                      link,
                      children: [
                        new TextRun({
                          text: post.message || "Post Facebook",
                          bold: false,
                          font: "Calibri",
                          size: 24,
                        }),
                      ],
                    }),
                  ],
                }),
              ],
            }),
          ],
        })
      );
    }

    const paragraphsVideos = [];
    for (const video of videos) {
      const link = `https://ahuncycle.com/video?videoUrl=${encodeURIComponent(
        video.videoUrl
      )}`;

      paragraphsVideos.push(
        new Paragraph({
          bullet: { level: 0 },
          children: [
            new ExternalHyperlink({
              link,
              children: [
                new TextRun({
                  text: video.title,
                  underline: {},
                  font: "Calibri",
                  size: 22,
                }),
              ],
            }),
          ],
        })
      );
    }

    const doc = new Document({
      styles: {
        default: { document: { run: { font: "Calibri", size: 22 } } },
      },
      sections: [
        {
          children: [
            new Paragraph({
              alignment: AlignmentType.CENTER,
              children: [
                new ImageRun({
                  data: await (await fetch("/images/logo.png")).arrayBuffer(),
                  transformation: { width: 200, height: 60 },
                }),
              ],
            }),
            new Paragraph({
              spacing: { after: 400 },
              alignment: AlignmentType.CENTER,
              children: [
                new TextRun({
                  text: `Infolettre pour la période du ${
                    dateDebut || "début"
                  } au ${dateFin}`,
                  bold: true,
                  font: "Calibri",
                  size: 28,
                }),
              ],
            }),

            new Paragraph({
              spacing: { after: 200 },
              children: [
                new TextRun({
                  text: "Nouveaux articles",
                  bold: true,
                  font: "Calibri",
                  size: 26,
                }),
              ],
            }),
            new Table({
              width: { size: 100, type: WidthType.PERCENTAGE },
              rows: rowsArticles,
            }),

            fbPosts.length > 0 &&
              new Paragraph({
                spacing: { before: 400, after: 200 },
                children: [
                  new TextRun({
                    text: "Messages Facebook",
                    bold: true,
                    font: "Calibri",
                    size: 26,
                  }),
                ],
              }),
            fbPosts.length > 0 &&
              new Table({
                width: { size: 100, type: WidthType.PERCENTAGE },
                rows: rowsFbPosts,
              }),

            new Paragraph({
              spacing: { before: 400, after: 200 },
              children: [
                new TextRun({
                  text: "Nouvelles vidéos",
                  bold: true,
                  font: "Calibri",
                  size: 26,
                }),
              ],
            }),
            ...paragraphsVideos,
          ].filter(Boolean),
        },
      ],
    });

    const blob = await Packer.toBlob(doc);
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = "infolettre.docx";
    a.click();
    URL.revokeObjectURL(url);
  };

  if (!user) return <Page404 />;

  return (
    <div className="stretch bgColor">
      <div className="container py-6">
        <div className="text-center mb-6">
          <h2 className="text-4xl font-semibold">Créer une infolettre</h2>
        </div>

        {/* DATES + SOUMETTRE */}
        <div className="flex gap-4 justify-center items-end mb-6">
          <div className="font-bold mt-2">Période :</div>

          <input
            type="date"
            value={dateDebutInput}
            onChange={(e) => setDateDebutInput(e.target.value)}
            className="border px-3 py-2 rounded"
          />

          <input
            type="date"
            value={dateFinInput}
            onChange={(e) => setDateFinInput(e.target.value)}
            className="border px-3 py-2 rounded"
          />

          <button
            onClick={handleSubmitDates}
            className="px-5 py-2 bg-primary-6000 text-white rounded hover:bg-primary-700 transition disabled:opacity-60"
            disabled={isLoading}
          >
            {isLoading ? "Chargement..." : "Soumettre"}
          </button>
        </div>

        {/* ERREURS (granulaires) */}
        <div className="max-w-3xl mx-auto mb-6 space-y-2">
          {errorArticles && (
            <p className="text-red-600 font-semibold text-center">
              {errorArticles}
            </p>
          )}
          {errorFb && (
            <p className="text-red-600 font-semibold text-center">{errorFb}</p>
          )}
          {errorVideos && (
            <p className="text-red-600 font-semibold text-center">
              {errorVideos}
            </p>
          )}
        </div>

        {/* ARTICLES */}
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6 mb-8">
          {articles.map((post) => (
            <Card key={post.id} post={post} />
          ))}
        </div>

        {/* FACEBOOK POSTS */}
        {fbPosts.length > 0 && (
          <div className="mb-8">
            <h3 className="text-2xl font-bold mb-4 text-center">
              Posts Facebook
            </h3>
            <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
              {fbPosts.map((post) => (
                <CardFb key={post._id || post.href} post={post} />
              ))}
            </div>
          </div>
        )}

        {/* VIDEOS */}
        {videos.length > 0 && (
          <div className="mb-8">
            <h3 className="text-2xl font-bold mb-4 text-center">
              Nouvelles vidéos
            </h3>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
              {videos.map((vid) => (
                <CardVideo key={vid.id} video={vid} />
              ))}
            </div>
          </div>
        )}

        {/* BUTTON */}
        <div className="flex justify-center mt-10">
          <button
            onClick={generateWord}
            className="px-6 py-3 bg-primary-6000 text-white rounded-full hover:bg-primary-700 transition disabled:opacity-60"
            disabled={isLoading}
          >
            Générer le fichier Word
          </button>
        </div>
      </div>
      <ChangeFbTokenButton />
    </div>
  );
};

export default PageCreerInfolettre;
